import { NewExperienceDialog } from '@/components/experiences/new-experience-dialog';
import { TemplateGallery } from '@/components/experiences/template-gallery';
import { PageHeader } from '@/components/shell/page-header';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDeleteExperience, useExperiences, type Experience } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/experiences')({ component: ExperiencesPage });

const SURFACE: Record<Experience['type'], string> = {
  modal: 'Modal',
  fullscreen: 'Fullscreen',
  banner: 'Banner',
  bottom_sheet: 'Bottom sheet',
  inline: 'Inline',
};

function ExperiencesPage(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const { data: experiences, isLoading } = useExperiences(appId);
  const remove = useDeleteExperience(appId);

  const [pendingDelete, setPendingDelete] = useState<Experience | null>(null);
  const [query, setQuery] = useState('');
  const rows = experiences?.filter((experience) => experience.name.toLowerCase().includes(query.toLowerCase())) ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Experiences"
        description="Design reusable mobile content, then connect it to a campaign for delivery."
        actions={experiences?.length ? <NewExperienceDialog /> : undefined}
      />

      {isLoading ? (
        <Skeleton className="h-40 rounded-lg" />
      ) : (
        <Tabs defaultValue={experiences?.length ? 'all' : 'templates'}>
          <TabsList aria-label="Experience views">
            <TabsTrigger value="all">All experiences</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            {!experiences?.length ? (
              <div className="rounded-xl border border-dashed px-6 py-14 text-center">
                <h2 className="font-medium">No experiences yet</h2>
                <p className="mt-1 text-sm text-muted-foreground">Choose the Templates tab to start from an outcome and a valid native document.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search experiences"
                    className="pl-9"
                  />
                </div>
                <div className="overflow-hidden rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Starts with</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead>Updated</TableHead>
                        <TableHead className="w-0" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((experience) => (
                        <TableRow key={experience.id}>
                          <TableCell className="font-medium">
                            <Link
                              to="/experiences/$id/edit"
                              params={{ id: experience.id }}
                              className="hover:underline"
                            >
                              {experience.name}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{SURFACE[experience.type] ?? experience.type}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">v{experience.version}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(experience.updated_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Delete experience"
                              className="text-muted-foreground hover:text-destructive"
                              onClick={() => setPendingDelete(experience)}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {rows.length === 0 ? <p className="border-t px-4 py-10 text-center text-sm text-muted-foreground">No experiences match “{query}”.</p> : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  “Starts with” reflects the first step only. Multi-step experiences may use several overlay surfaces.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="templates" className="mt-6">
            <TemplateGallery />
          </TabsContent>
        </Tabs>
      )}

      <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              An experience a campaign still points at cannot be deleted — the server refuses and tells you
              which campaign. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                const experience = pendingDelete;
                if (!experience) return;
                remove.mutate(experience.id, {
                  onSuccess: () => toast.success('Experience deleted'),
                  // The 409 body carries the reason (a campaign links it). `api` already surfaces the
                  // server's message — showing a generic failure here would hide the one useful fact.
                  onError: (error: Error) => toast.error(error.message),
                });
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
